/**
 * 图标改色后处理（CSS mask 单色渲染）
 *
 * 约定：组件变量中存在 `iconColor` / `xxxIconColor` 且值非空时，
 * 同级同名去掉 `Color` 后缀的变量（`icon` / `xxxIcon`）视为图标 URL。
 * 渲染产物 HTML 中 src 匹配该 URL 的 <img> 会被替换为
 * `<span class="cms-icon-mono" style="--icon-url:...;--icon-color:...">`，
 * 由 CSS mask 渲染为单色剪影；未设置颜色的图标保持原样。
 *
 * 说明：
 * - mask 按 alpha 通道工作，透明底 PNG 同样可被染色为剪影；
 * - 递归遍历变量（支持 list 子项的 icon/iconColor 配对）；
 * - 仅当 HTML 中存在 <img> 时才执行，CSS 渲染零开销；
 * - 已有独立配色约定的字段（如 CmsBannerCarousel 的 bulletIconColor，
 *   其 bulletIcon 并非 URL 变量）不会命中配对规则，不受影响。
 */

/** .cms-icon-mono 基础样式：SSR/预览场景需随 css 一并注入 */
export const CMS_ICON_MONO_CSS =
  '.cms-icon-mono{display:inline-block;width:1em;height:1em;background-color:var(--icon-color,currentColor);-webkit-mask:var(--icon-url) center/contain no-repeat;mask:var(--icon-url) center/contain no-repeat;}'

const COLOR_SUFFIX = 'Color'

function escapeRegExp(input: string): string {
  return input.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/** 颜色值净化：仅保留颜色字符，防止经 style 注入 */
function sanitizeColor(raw: unknown): string {
  return String(raw ?? '')
    .trim()
    .replace(/["'<>\\;]/g, '')
}

/** URL 中引号转义，保证可安全放入 url('...') */
function sanitizeUrlForCss(raw: string): string {
  return raw.trim().replace(/'/g, '%27')
}

/** 递归收集 (图标URL, 颜色) 配对 */
function collectIconPairs(
  vars: unknown,
  out: Array<{ url: string; color: string }>,
  seen: Set<object>,
): void {
  if (!vars || typeof vars !== 'object') return
  if (seen.has(vars)) return
  seen.add(vars)

  if (Array.isArray(vars)) {
    vars.forEach((item) => collectIconPairs(item, out, seen))
    return
  }

  const record = vars as Record<string, unknown>
  for (const [key, value] of Object.entries(record)) {
    if (!key.endsWith(COLOR_SUFFIX) || key.length <= COLOR_SUFFIX.length) continue
    const color = sanitizeColor(value)
    if (!color) continue
    const baseKey = key.slice(0, -COLOR_SUFFIX.length)
    const url = record[baseKey]
    if (typeof url === 'string' && url.trim()) {
      out.push({ url: url.trim(), color })
    }
  }
  for (const value of Object.values(record)) {
    if (value && typeof value === 'object') collectIconPairs(value, out, seen)
  }
}

/** 将 src 匹配指定 URL 的 <img> 替换为 mask 单色节点，并沿用其 class/style 保证原布局 */
function replaceImgWithMono(html: string, url: string, color: string): string {
  const urlEscaped = escapeRegExp(url)
  const urlHtmlEscaped = escapeRegExp(url.replace(/&/g, '&amp;'))
  const srcPattern = urlEscaped === urlHtmlEscaped ? urlEscaped : `(?:${urlEscaped}|${urlHtmlEscaped})`
  const imgRe = new RegExp(`<img\\b[^>]*\\ssrc=["']${srcPattern}["'][^>]*>`, 'gi')

  const safeUrl = sanitizeUrlForCss(url)
  return html.replace(imgRe, (imgTag) => {
    const cls = /class=["']([^"']*)["']/i.exec(imgTag)?.[1] ?? ''
    const style = /style=["']([^"']*)["']/i.exec(imgTag)?.[1] ?? ''
    const monoStyle = [`--icon-url:url('${safeUrl}')`, `--icon-color:${color}`, style]
      .filter(Boolean)
      .join(';')
    return `<span class="cms-icon-mono${cls ? ` ${cls}` : ''}" style="${monoStyle}" aria-hidden="true"></span>`
  })
}

/**
 * 渲染后处理：按 icon/xxxColor 配对，将匹配的 <img> 替换为 mask 单色节点。
 * 无 <img>（如 CSS 渲染）或无配对时原样返回。
 */
export function applyIconColors(html: string, vars: unknown): string {
  if (!html || !html.includes('<img')) return html
  const pairs: Array<{ url: string; color: string }> = []
  collectIconPairs(vars, pairs, new Set())
  if (!pairs.length) return html
  let out = html
  for (const { url, color } of pairs) {
    out = replaceImgWithMono(out, url, color)
  }
  return out
}
